import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import MainLayout from "../components/layout/MainLayout";
import NursingOPDAssessmentList from "./OPD/Nursing/NursingOPDAssessmentList";
import Breadcrumbs from "../components/common/Breadcrumbs";
import { selectHasPermission } from "../store/slices/authSlice";
import Swal from 'sweetalert2';
import { fetchNursingOPDAssessments, deleteNursingOPDAssessment } from "../store/slices/nursingOPDAssessmentsSlice";

const NursingOPDAssessments = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { nursingOPDAssessments, isLoading } = useSelector((state) => state.nursingOPDAssessments);

    const canManageNursingOPD = useSelector((state) => selectHasPermission(state, "nursing-opd-assessment-manage"));
    const canCreateNursingOPD = useSelector((state) => selectHasPermission(state, "nursing-opd-assessment-create"));
    const canEditNursingOPD = useSelector((state) => selectHasPermission(state, "nursing-opd-assessment-edit"));
    const canDeleteNursingOPD = useSelector((state) => selectHasPermission(state, "nursing-opd-assessment-delete"));

    useEffect(() => {
        if (canManageNursingOPD) {
            dispatch(fetchNursingOPDAssessments());
        }
    }, [dispatch, canManageNursingOPD]);

    if (!canManageNursingOPD) {
        return (
            <Navigate to="/dashboard" state={{ error: "No permission to manage Nursing OPD Assessments." }} replace />
        );
    }

    const handleAdd = () => navigate("/nursing-opd-assessments/add");
    const handleEdit = (id) => navigate(`/nursing-opd-assessments/edit/${id}`);

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You want to delete this Nursing Assessment?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            customClass: {
                confirmButton: 'btn btn-primary me-3',
                cancelButton: 'btn btn-label-secondary'
            },
            buttonsStyling: false
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteNursingOPDAssessment(id)).unwrap();
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Nursing Assessment has been deleted.',
                    customClass: { confirmButton: 'btn btn-success' }
                });
            } catch (err) {
                console.error("Failed to delete assessment:", err);
                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: err || 'Failed to delete assessment.',
                    customClass: { confirmButton: 'btn btn-danger' }
                });
            }
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        {isLoading ? (
                            <div className="d-flex justify-content-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <NursingOPDAssessmentList
                                assessments={nursingOPDAssessments}
                                onEdit={canEditNursingOPD ? handleEdit : null}
                                onDelete={canDeleteNursingOPD ? handleDelete : null}
                                onAddAssessment={canCreateNursingOPD ? handleAdd : null}
                            />
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default NursingOPDAssessments;
